import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { AppTheme } from '../../AppTheme';
import { Item } from '@/types/item.types';
import { createItem } from '../../service/item/ItemServices';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { showToast } from '../../store/slices/toastSlice';
import { getAllSACDetails } from '../../service/sacDetails/SacDetailsService';
import { Toast } from 'primereact/toast';

// Item type options
const itemTypeOptions = [
  { label: 'Product', value: 'product' },
  { label: 'Service', value: 'service' },
  { label: 'Hourly Rate', value: 'hourly' },
  { label: 'Project Phase', value: 'project_phase' }
];

// Unit type options
const unitTypeOptions = [
  { label: 'Each', value: 'each' },
  { label: 'Hour', value: 'hour' },
  { label: 'Day', value: 'day' },
  { label: 'Project', value: 'project' }
];

// Industry options
const industryOptions = [
  { label: 'Technology', value: 1 },
  { label: 'Manufacturing', value: 2 },
  { label: 'Healthcare', value: 3 },
  { label: 'Financial Services', value: 4 },
  { label: 'Retail', value: 5 },
  { label: 'Education', value: 6 },
  { label: 'Construction', value: 7 },
  { label: 'Entertainment', value: 8 },
  { label: 'Hospitality', value: 9 },
  { label: 'Other', value: 10 }
];

// Currency options (you may need to fetch these from API)
const currencyOptions = [
  { label: 'USD - US Dollar', value: 1 },
  { label: 'EUR - Euro', value: 2 },
  { label: 'GBP - British Pound', value: 3 },
  { label: 'CAD - Canadian Dollar', value: 4 },
  { label: 'AUD - Australian Dollar', value: 5 }
];

// Tax rate options (you may need to fetch these from API)
const taxRateOptions = [
  { label: 'Sales Tax (10%)', value: 1 },
  { label: 'VAT (20%)', value: 2 },
  { label: 'GST (5%)', value: 3 },
  { label: 'No Tax (0%)', value: 4 }
];

const gstRateOptions = [
  { label: "0%", value: 0 },
  { label: "5%", value: 5 },
  { label: "10%", value: 10 },
  { label: "12%", value: 12 },
  { label: "18%", value: 18 },
  { label: "20%", value: 20 },
  { label: "28%", value: 28 },
  { label: "30%", value: 30 },
];

export const CreateItem: React.FC = () => {
  const localization = useSelector((state: any) => state.localization);
  const [errors, setErrors] = useState<{ item_name?: boolean, sac_gst_rate?: boolean }>({ item_name: false, sac_gst_rate: false })
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const toast = useRef<Toast>(null);
  const [item, setItem] = useState<Partial<Item>>({
    item_code: '',
    item_name: '',
    description: '',
    item_type: 'service',
    unit_price: "0",
    unit_type: 'hour',
    currency_id: 1, // Default to USD
    // tax_rate_id: 1, // Default tax rate
    sac_gst_rate: 0,
    sacCode: null,
    industry_id: undefined,
    is_active: true
  });

  const handleChange = (field: keyof Item, value: any) => {
    setItem(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for the field if it exists
    if (field === "item_name") {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    if (!item.item_name) {
      setErrors({ ...errors, item_name: true })
      dispatch(showToast({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Item name is required',
        life: 3000
      }));
      return false;
    }

    if (parseInt(item?.unit_price || "0") === undefined || parseInt(item?.unit_price || "0") <= 0 ) {
      dispatch(showToast({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Unit price must be greater than zero',
        life: 3000
      }));
      return false;
    }

    if (!item.unit_type) {
      dispatch(showToast({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Unit type is required',
        life: 3000
      }));
      return false;
    }

    if (item.sac_gst_rate===null || item.sac_gst_rate===undefined) {
      setErrors({ ...errors, sac_gst_rate: true })
      dispatch(showToast({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Service tax is required',
        life: 3000
      }));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await createItem(dispatch, [{
        item_name: item.item_name || "",
        description: item.description,
        item_type: item.item_type || "",
        unit_price: parseFloat(item.unit_price || "0"),
        unit_type: item.unit_type,
        currency_id: item.currency_id,
        sacCode: item?.sacCode,
        sac_gst_rate: item.sac_gst_rate
      }]);

      if (response) {
        dispatch(showToast({
          severity: 'success',
          summary: 'Success',
          detail: 'Item created successfully',
          life: 3000
        }));

        setTimeout(() => {
          navigate('/item');
        }, 500);
      } else {
        throw new Error(response?.error?.message || 'Failed to create item');
      }
    } catch (error) {
      console.error('Error creating item:', error);
      dispatch(showToast({
        severity: 'error',
        summary: 'Error',
        detail: error instanceof Error ? error.message : 'Failed to create item. Please try again.',
        life: 3000
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Conditional rendering for appropriate unit type options based on item type
  const getUnitTypeOptions = () => {
    switch (item.item_type) {
      case 'product':
        return unitTypeOptions.filter(option => option.value === 'each');
      case 'service':
        return unitTypeOptions.filter(option => ['hour', 'day', 'project'].includes(option.value));
      case 'hourly':
        return unitTypeOptions.filter(option => option.value === 'hour');
      case 'project_phase':
        return unitTypeOptions.filter(option => option.value === 'project');
      default:
        return unitTypeOptions;
    }
  };

  const handleInvalidValue = () => {
    toast.current?.show({
      severity: "warn",
      summary: "Invalid GST Rate",
      detail: "GST rate cannot be negative. Reset to 0%.",
      life: 3000,
    });
    handleChange('sac_gst_rate', 0)
    handleChange('sacCode', null)
  };

  return (
    <div>
      <Toast ref={toast} />
      <div className="flex justify-between items-center mb-0">
        <h1 className="text-xl font-bold surface-900 mb-4">Create New Item</h1>

        <Button
          className="p-button-outlined"
          onClick={() => navigate('/item')}
        >
          <FontAwesomeIcon icon={faXmark} />{"  "}
          Cancel
        </Button>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="mb-4 card_content_padding">
          <div className="p-fluid grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-2">
              <h2 className="text-xl font-semibold mb-3 mt-0">Item Information</h2>
            </div>

            <div className="field">
              <label htmlFor="item_name" className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <InputText
                id="item_name"
                value={item.item_name}
                onChange={(e) => handleChange('item_name', e.target.value)}
                className={!item.item_name ? 'p-invalid' : ''}
                required
              />
              {errors.item_name && (
                <small className="p-error">Item name is required</small>
              )}
            </div>

            <div className="field">
              <label htmlFor="unit_price" className="block text-sm font-medium text-gray-700 mb-1">Unit Price *</label>
              <InputNumber
                id="unit_price"
                value={parseInt(item?.unit_price || "0")}
                onValueChange={(e) => handleChange('unit_price', e.value)}
                mode="currency"
                currency="USD"
                locale="en-US"
                minFractionDigits={2} 
                className={parseInt(item?.unit_price || "0") === undefined || parseInt(item?.unit_price || "0") < 0 ? 'p-invalid' : ''}
                required
              />
              {(parseInt(item?.unit_price || "0") === undefined || parseInt(item?.unit_price || "0") < 0) && (
                <small className="p-error">Unit price must be a non-negative number</small>
              )}
            </div>

            <div className="field">
              <label htmlFor="unit_type" className="block text-sm font-medium text-gray-700 mb-1">Unit Type *</label>
              <Dropdown
                id="unit_type"
                value={item.unit_type}
                options={getUnitTypeOptions()}
                onChange={(e) => handleChange('unit_type', e.value)}
                placeholder="Select Unit Type"
                className={!item.unit_type ? 'p-invalid' : ''}
                required
              />
              {!item.unit_type && (
                <small className="p-error">Unit type is required</small>
              )}
            </div>
            <div className="field">
              <label htmlFor="unit_type" className="block text-sm font-medium text-gray-700 mb-1">Service Tax</label>
              <Dropdown
                value={`${(item.sac_gst_rate)}%`}
                options={gstRateOptions}
                onChange={(e) => {
                  const rawValue = e.target?.value ?? e.value;

                  const stringValue = String(rawValue); // Ensure it's a string

                  const cleanInput = stringValue.includes('%')
                    ? stringValue.replace('%', '')?.trim()
                    : stringValue?.trim();

                  handleChange('sac_gst_rate', cleanInput);
                  setErrors({ ...errors, sac_gst_rate: false });
                }}
                placeholder={localization?.SelectGSTRate || "Select GST Rate"}
                className="w-full"
                editable={true}
                onBlur={(e) => {
                  // Safely try to get the input
                  const rawInput =
                    e?.target?.value ??
                    e?.target?.innerText ??
                    item.sac_gst_rate?.toString() ??
                    "";

                  const cleaned = rawInput?.includes('%') ? rawInput?.replace("%", "")?.trim() : rawInput?.trim();;

                  if (cleaned === "") {
                    handleChange('sac_gst_rate', 0)
                    handleChange('sacCode', null)
                    return;
                  }

                  const numeric = parseFloat(cleaned);

                  if (isNaN(numeric)) {
                    handleChange('sac_gst_rate', 0)
                    handleChange('sacCode', null)
                    return;
                  }

                  if (numeric < 0) {
                    handleInvalidValue();
                  } else {
                    handleChange('sac_gst_rate', numeric)
                    handleChange('sacCode', null)
                  }
                }}
              />


              {errors.sac_gst_rate && (
                <small className="p-error">Service tax is required</small>
              )}
            </div>

            <div className="field col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <InputTextarea
                id="description"
                value={item.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={5}
              />
            </div>
          </div>
        </Card>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            className="p-button-outlined"
            onClick={() => navigate('/item')}
          >
            <FontAwesomeIcon icon={faXmark} />{"  "}
            Cancel
          </Button>
          <Button
            type="submit"
            icon="pi pi-check"
            isLoading={isSubmitting}
          >
            <FontAwesomeIcon icon={faCheck} />{"  "}
            Create Item
          </Button>
        </div>
      </form>
    </div>
  );
};